/**
 * Default values, null-safe fallbacks, and presentation metadata.
 *
 * The backend can return null/partial data (fresh merchants, missing reason
 * lists). Helpers here guarantee the UI always has something sensible to show —
 * including a default reason used for cancellation.
 */
import type {
  AutopilotActionType,
  AutopilotOperator,
  MerchantSettings,
  RefundMode,
  RefundTiming,
  ReturnMethod,
  ReturnReason,
  ReturnStatus,
  TemplateChannel,
} from '@/types';

// ── Return reasons (null-safe) ──────────────────────────────────────────────
export const DEFAULT_RETURN_REASONS: ReturnReason[] = [
  { slug: 'wrong-size', label: 'Wrong size', photoRequired: false, isActive: true, autoApprove: true, sortOrder: 1 },
  { slug: 'defective', label: 'Defective / damaged', photoRequired: true, isActive: true, autoApprove: false, sortOrder: 2 },
  { slug: 'not-as-described', label: 'Not as described', photoRequired: true, isActive: true, autoApprove: false, sortOrder: 3 },
  { slug: 'wrong-colour', label: 'Wrong colour', photoRequired: false, isActive: true, autoApprove: true, sortOrder: 4 },
  { slug: 'changed-my-mind', label: 'Changed my mind', photoRequired: false, isActive: true, autoApprove: true, sortOrder: 5 },
  { slug: 'wrong-item', label: 'Wrong item received', photoRequired: true, isActive: true, autoApprove: false, sortOrder: 6 },
  { slug: 'late-delivery', label: 'Delivered too late', photoRequired: false, isActive: true, autoApprove: true, sortOrder: 7 },
];

/** Always returns a usable reason list — defaults when backend gives null/empty. */
export function resolveReturnReasons(reasons: ReturnReason[] | null | undefined): ReturnReason[] {
  if (!Array.isArray(reasons)) {
    return DEFAULT_RETURN_REASONS;
  }
  const usable = reasons.filter((reason) => reason && typeof reason.slug === 'string' && reason.slug.length > 0);
  return usable.length > 0 ? usable : DEFAULT_RETURN_REASONS;
}

const REASON_LABEL_BY_SLUG: Record<string, string> = Object.fromEntries(
  DEFAULT_RETURN_REASONS.map((reason) => [reason.slug, reason.label]),
);

/** Human label for a reason slug, falling back to a title-cased slug. */
export function reasonLabel(slug: string | null | undefined): string {
  if (!slug) {
    return '—';
  }
  return REASON_LABEL_BY_SLUG[slug] ?? slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Reason applied when cancelling a return and no reason list is configured. */
export const DEFAULT_CANCELLATION_REASON = 'Changed my mind';

/** Picklist for the merchant cancel/decline dialog, defaulted when null. */
export const DEFAULT_DECLINE_REASONS: string[] = [
  'Return window expired',
  'Final sale item',
  'Condition unacceptable',
  'Suspected fraud',
  'Other',
];

// ── Settings fallback ───────────────────────────────────────────────────────
export const DEFAULT_MERCHANT_SETTINGS: MerchantSettings = {
  returnWindowDays: 15,
  returnWindowFrom: 'ORDER_DATE',
  exchangeEnabled: true,
  exchangeWindowDays: 30,
  exchangeWindowFrom: 'ORDER_DATE',
  refundTiming: 'AFTER_QC',
  restockingFeePct: 0,
  returnShippingFee: 0,
  editPickupAddress: true,
  maxReturnsPerCustomerPerMonth: 3,
  maxItemsPerReturnRequest: 10,
  nonReturnableCategories: [],
  nonReturnableSkuPatterns: [],
  nonReturnableCollectionIds: [],
  greenReturnsEnabled: false,
  greenReturnsThreshold: 500,
  greenReturnsExcludedReasons: [],
  qcEnabled: false,
  refundModeOriginal: true,
  refundModeStoreCredit: true,
  refundModeGiftCard: false,
  refundModeDiscountCode: false,
  storeCreditBonusPct: 0,
  giftCardBonusPct: 0,
  discountCodeBonusPct: 0,
  customerEmailEnabled: true,
  customerSmsEnabled: false,
  customerWhatsappEnabled: false,
  merchantNotifyEmail: '',
  merchantPhone: '',
  timezone: 'Asia/Kolkata',
  displayCurrency: 'INR',
  fetchShiprocketWarehouse: false,
  autoBlockEnabled: false,
  autoBlockThreshold: 3,
  isLive: false,
  portalPrimaryColor: '#1A1A1A',
  portalAccentColor: '#4F46E5',
  portalTitle: 'Returns & Exchanges',
  portalSubtitle: 'Easy, hassle-free returns',
};

// ── Status presentation ─────────────────────────────────────────────────────
export type StatusTone = 'pending' | 'progress' | 'success' | 'danger' | 'neutral';

export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

const STATUS_META: Record<ReturnStatus, StatusMeta> = {
  PENDING_REVIEW: { label: 'Pending review', tone: 'pending' },
  AUTO_APPROVED: { label: 'Auto-approved', tone: 'success' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  PICKUP_SCHEDULED: { label: 'Pickup scheduled', tone: 'progress' },
  PICKED_UP: { label: 'Picked up', tone: 'progress' },
  IN_TRANSIT: { label: 'In transit', tone: 'progress' },
  RECEIVED_AT_WAREHOUSE: { label: 'At warehouse', tone: 'progress' },
  PENDING_QC: {label: 'Pending QC', tone: 'progress'},
  QC_PASSED: { label: 'QC passed', tone: 'success' },
  QC_FAILED: { label: 'QC failed', tone: 'danger' },
  QC_FAILED_PENDING_REVIEW: { label: 'QC failed — review', tone: 'pending' },
  REFUNDED: { label: 'Refunded', tone: 'success' },
  EXCHANGED: { label: 'Exchanged', tone: 'success' },
  CLOSED: { label: 'Closed', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

const UNKNOWN_STATUS_META: StatusMeta = { label: 'Processing', tone: 'neutral' };

export function getStatusMeta(status: ReturnStatus | string | null | undefined): StatusMeta {
  if (status && status in STATUS_META) {
    return STATUS_META[status as ReturnStatus];
  }
  return UNKNOWN_STATUS_META;
}

/** Saved-view tabs for the returns list (label → status filter). */
export const RETURN_STATUS_TABS: { label: string; status?: string; statuses?: string }[] = [
  { label: 'All' },
  { label: 'Awaiting approval', status: 'PENDING_REVIEW' },
  { label: 'Auto Approved', status: 'AUTO_APPROVED' },
  { label: 'Approved', status: 'APPROVED' },
  { label: 'Pickup scheduled', status: 'PICKUP_SCHEDULED' },
  { label: 'Picked up', status: 'PICKED_UP' },
  { label: 'In transit', status: 'IN_TRANSIT' },
  { label: 'At warehouse', status: 'RECEIVED_AT_WAREHOUSE' },
  { label: 'Pending QC', status: 'PENDING_QC'},
  { label: 'QC failed', statuses: 'QC_FAILED,QC_FAILED_PENDING_REVIEW' },
  { label: 'Refunded', status: 'REFUNDED' },
  { label: 'Rejected', status: 'REJECTED' },
  { label: 'Cancelled', status: 'CANCELLED' },
];

// ── Mode / method / timing labels ───────────────────────────────────────────
export const REFUND_MODE_LABELS: Record<RefundMode, string> = {
  ORIGINAL_PAYMENT: 'Original payment',
  STORE_CREDIT: 'Store credit',
  GIFT_CARD: 'Gift card',
  DISCOUNT_CODE: 'Discount code',
  PARTIALLY_EXCHANGE: 'Partially exchanged',
  EXCHANGE: 'Exchanged',
};

export const REFUND_MODE_OPTIONS: RefundMode[] = [
  'ORIGINAL_PAYMENT',
  'STORE_CREDIT',
  'GIFT_CARD',
  'DISCOUNT_CODE',
  'PARTIALLY_EXCHANGE',
  'EXCHANGE',
];

export function refundModeLabel(mode: RefundMode | string | null | undefined): string {
  if (mode && mode in REFUND_MODE_LABELS) {
    return REFUND_MODE_LABELS[mode as RefundMode];
  }
  return mode ? String(mode) : '—';
}

export const RETURN_METHOD_LABELS: Record<ReturnMethod, string> = {
  PICKUP: 'Courier pickup',
  DROPOFF: 'Store drop-off',
  SELF_SHIP: 'Self ship',
};

export const REFUND_TIMING_OPTIONS: { value: RefundTiming; label: string; description: string }[] = [
  { value: 'INSTANT_APPROVE', label: 'On approval', description: 'Refund as soon as the return is approved.' },
  { value: 'AFTER_PICKUP', label: 'After pickup', description: 'Refund once the courier collects the item.' },
  { value: 'AFTER_QC', label: 'After QC', description: 'Refund after the item passes inspection (default).' },
];

// ── Exception severity ──────────────────────────────────────────────────────
export const EXCEPTION_SEVERITY_TONE: Record<string, StatusTone> = {
  LOW: 'neutral',
  MEDIUM: 'pending',
  HIGH: 'danger',
};

// ── Autopilot builder options ───────────────────────────────────────────────
export const AUTOPILOT_CONDITION_FIELDS: { value: string; label: string }[] = [
  { value: 'itemSubtotal', label: 'Item subtotal' },
  { value: 'netRefund', label: 'Net refund' },
  { value: 'reasonSlug', label: 'Reason' },
  { value: 'itemCount', label: 'Item count' },
  // { value: 'ndrCount', label: 'NDR count' },
  { value: 'refundMode', label: 'Refund mode' },
  { value: 'customerEmail', label: 'Customer email' },
  { value: 'pincode', label: 'Pincode' },
  // { value: 'isExchange', label: 'Is exchange' },
];

export const AUTOPILOT_OPERATORS: { value: AutopilotOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'contains', label: 'contains' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
];

export const AUTOPILOT_ACTION_TYPES: { value: AutopilotActionType; label: string }[] = [
  { value: 'AUTO_APPROVE', label: 'Auto-approve' },
  { value: 'REJECT', label: 'Reject' },
  // { value: 'FLAG_FOR_QC', label: 'Flag for QC' },
  { value: 'SCHEDULE_PICKUP', label: 'Schedule pickup' },
  { value: 'ISSUE_REFUND', label: 'Issue refund' },
 // { value: 'APPLY_RESTOCKING_FEE', label: 'Apply restocking fee' },
  // { value: 'SEND_NOTIFICATION', label: 'Send notification' },
  { value: 'ADD_TO_BLOCKLIST', label: 'Add to blocklist' },
];

export const AUTOPILOT_TRIGGER_EVENTS: string[] = ['RETURN_SUBMITTED'];

// ── Notification templates ──────────────────────────────────────────────────
export const TEMPLATE_KEYS: string[] = [
  'return_submitted',
  'return_approved',
  'pickup_scheduled',
  'picked_up',
  'received_at_warehouse',
  'refund_processed',
  'exception_raised',
  'qc_failed',
];

export const TEMPLATE_CHANNELS: TemplateChannel[] = ['SMS', 'EMAIL', 'WHATSAPP'];

export const TEMPLATE_PLACEHOLDERS: string[] = [
  '{{customerName}}',
  '{{orderNumber}}',
  '{{returnId}}',
  '{{status}}',
  '{{refundMode}}',
  '{{netRefund}}',
  '{{awb}}',
];

/** Apply the fallback settings over a possibly-partial backend response. */
export function resolveSettings(settings: Partial<MerchantSettings> | null | undefined): MerchantSettings {
  return { ...DEFAULT_MERCHANT_SETTINGS, ...(settings ?? {}) };
}
